import React, { useRef, useState } from "react";
import { Button, Input, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import utils from "utils";
import { CLIENT_ID } from "constants/ApiEndPointsConstants";
import { getSurveyTemplateById } from "./educationApi";

// Shared helpers for the four generic Survey tabs (Survey Templates, Facility
// Assignments, Facility Surveys, Survey History) — ported from the old app's
// surveyTemplate.store.js / surveySubmission.store.js / QuestionField.js.

export const SURVEY_DATE_FORMAT = "DD-MM-YYYY";
export const SURVEY_DATE_TIME_FORMAT = "DD-MM-YYYY HH:mm";
export const SURVEY_MONTH_LABEL_FORMAT = "MMM YYYY";
export const SURVEY_MONTH_KEY_FORMAT = "YYYY-MM";

// Templates/assignments/submissions are scoped per client_id on the backend
// (default 1) — same id educationAuth sends as the client-id header.
export const getSurveyClientId = () => Number(localStorage.getItem(CLIENT_ID)) || 1;

// The survey controllers answer 200 with success:false for validation errors
// and for "no records", so the transport-level `error` flag isn't enough.
export const isSurveyOk = (response) => !!response && !response.error && !!response.result && response.result.success !== false;

export const getSurveyList = (response) => (isSurveyOk(response) && Array.isArray(response.result.result) ? response.result.result : []);

// Write calls: toast the backend's own message either way (the old app's
// getValidDataFromResponse behaviour) and report whether it succeeded.
export const notifySurveyResult = (response, successFallback) => {
  if (isSurveyOk(response)) {
    message.success(response.result.message || successFallback);
    return true;
  }
  message.error(response?.result?.message || "Something went wrong");
  return false;
};

export const QUESTION_TYPE_OPTIONS = [
  { label: "Short Text", value: "text" },
  { label: "Long Text", value: "textarea" },
  { label: "Single Choice", value: "radio" },
  { label: "Multiple Choice", value: "checkbox" },
];

export const CHOICE_QUESTION_TYPES = ["radio", "checkbox"];

// `options` reaches the UI either as a real array (already parsed) or as the
// raw JSON string the mysql driver hands back, and each entry can be a plain
// string or a `{ value, label, requires_specify }` object. Normalize all of it.
export const normalizeOptions = (options) => {
  let parsed = options;

  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch (error) {
      parsed = [];
    }
  }

  if (!Array.isArray(parsed)) return [];

  return parsed
    .filter((option) => option !== null && option !== undefined && option !== "")
    .map((option) => {
      if (typeof option === "object") {
        const value = option.value !== undefined && option.value !== "" ? option.value : option.label;
        return {
          value: `${value === undefined || value === null ? "" : value}`,
          label: `${option.label === undefined || option.label === null || option.label === "" ? value : option.label}`,
          requires_specify: !!option.requires_specify,
        };
      }

      return { value: `${option}`, label: `${option}`, requires_specify: false };
    })
    .filter((option) => option.value !== "");
};

// Stable form field name for a question (builder previews have no id yet).
export const getQuestionFieldName = (question = {}, index = 0) => {
  const id = question.id !== undefined && question.id !== null && question.id !== "" ? question.id : `new_${index}`;
  return `question_${id}`;
};

export const getSpecifyFieldName = (question = {}, index = 0) => `${getQuestionFieldName(question, index)}_specify`;

// The backend may hand a template back either flat (`{ ...header, questions: [] }`)
// or split (`{ template: {...}, questions: [] }`). Normalize both into the flat
// shape DynamicSurveyForm consumes.
export const normalizeTemplateResult = (result) => {
  if (!result) return null;

  const payload = Array.isArray(result) ? result[0] : result;

  if (!payload) return null;

  const header = payload.template && typeof payload.template === "object" ? payload.template : payload;
  const questions = payload.questions || payload.template_questions || header.questions || [];

  return { ...header, questions: Array.isArray(questions) ? questions : [] };
};

export const loadSurveyTemplate = async (templateId) => {
  const response = await getSurveyTemplateById(templateId);
  return isSurveyOk(response) ? normalizeTemplateResult(response.result.result) : null;
};

// Submission history can arrive either already nested (`[{ id, answers: [...] }]`)
// or flat, one row per answer with a `submission_id`. Collapse the flat shape
// into the nested one.
export const normalizeSubmissions = (result) => {
  if (!Array.isArray(result) || result.length === 0) return [];

  const hasNestedAnswers = result.some((row) => Array.isArray(row.answers));

  if (hasNestedAnswers) {
    return result.map((row) => ({ ...row, answers: Array.isArray(row.answers) ? row.answers : [] }));
  }

  const isFlatAnswerRow = result.some((row) => row.submission_id !== undefined && (row.question_text !== undefined || row.question_id !== undefined));

  if (!isFlatAnswerRow) {
    return result.map((row) => ({ ...row, answers: [] }));
  }

  const grouped = [];
  const indexBySubmissionId = {};

  result.forEach((row) => {
    const key = `${row.submission_id}`;

    if (indexBySubmissionId[key] === undefined) {
      indexBySubmissionId[key] = grouped.length;
      grouped.push({
        id: row.submission_id,
        respondent_name: row.respondent_name,
        respondent_type: row.respondent_type,
        submission_month: row.submission_month,
        created_date: row.submission_created_date || row.created_date,
        answers: [],
      });
    }

    grouped[indexBySubmissionId[key]].answers.push({
      question_id: row.question_id,
      question_text: row.question_text,
      question_type: row.question_type,
      answer_text: row.answer_text,
      answer_json: row.answer_json,
    });
  });

  return grouped;
};

// Per-column search + sort, same as the old app's useTableFilter hook.
export const useColumnSearch = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const columnSearch = (dataIndex, placeholder = "") => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search by ${placeholder}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)} icon={<SearchOutlined />} size="small" style={{ width: 90, marginRight: 8 }}>
          Search
        </Button>
        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#2F6FE0" : undefined }} />,
    onFilter: (value, record) => `${record[dataIndex] ?? ""}`.toLowerCase().includes(`${value}`.toLowerCase()),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current && searchInput.current.select());
    },
    sorter: (a, b) => utils.antdTableSorter(a, b, dataIndex),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter highlightStyle={{ backgroundColor: "#FDECC8", padding: 0 }} searchWords={[searchText]} autoEscape textToHighlight={`${text ?? ""}`} />
      ) : (
        text
      ),
  });

  return columnSearch;
};

// Old app scrolled back to the top whenever a survey table paged/sorted/filtered.
export const scrollToTopOnTableChange = () => {
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, 100);
};
